import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Box, Badge, Typography } from "@mui/material";
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilCallback } from 'recoil';
import { atom_tourState, atom_originalTourState, atom_routeToStopLocation, atom_isNavigationMode, atom_showNavigationBubble, atom_currentNavigationStep, atom_isDesktop } from '@app/shared/state/tour.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { selector } from 'recoil';

// Only re-renders MapFilters when filter mode actually toggles, not on every listMode change
const selector_isFilterActive = selector({
  key: 'isFilterActive',
  get: ({ get }) => get(atom_listModeState) === 'filter',
});
import { atom_bookmarkedStopsState, atom_totalSavedCountState, atom_savedFilterActive } from '@app/shared/state/filter.atom';
import { atom_plannedRoute } from '@app/shared/state/show-content-modal.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { useTranslation } from 'react-i18next';
import { Stop, Tour } from '@app/shared/models/tour.type';
import { atom_flyToFitStops } from '@app/shared/state/map-ref.atom';
import { passiveCollector } from '@app/shared/services/passive-data/passive-data-collector';
// import { PassiveDataDebugPanel } from '@app/shared/services/passive-data/PassiveDataDebugPanel';
import { useNavigatorControls } from '@app/shared/hooks/useNavigatorControls';
import { MapStopSearch } from './MapStopSearch';
import { useRenderTracker } from '@app/shared/debug';
import { flyAfterLayout } from '@app/shared/utils/fly-after-layout';

export const MapFilters = React.memo(function MapFilters({ showAppBar, sidebarWidth }: { showAppBar: boolean, sidebarWidth: number }) {
  useRenderTracker('MapFilters');
  const { t, i18n } = useTranslation();
  const activeTour = useRecoilValue(atom_tourState);
  const originalTour = useRecoilValue(atom_originalTourState);
  const isFilterActive = useRecoilValue(selector_isFilterActive);
  const setListMode = useSetRecoilState(atom_listModeState);
  const totalSavedCount = useRecoilValue(atom_totalSavedCountState);
  const routeToStopState = useRecoilValue(atom_routeToStopLocation);
  const setIsNavigationMode = useSetRecoilState(atom_isNavigationMode);
  const showNavigationBubble = useRecoilValue(atom_showNavigationBubble);
  const currentNavigationStep = useRecoilValue(atom_currentNavigationStep);

  // On-demand reads for handler-only atoms (no subscription = no re-renders)
  const getHandlerAtoms = useRecoilCallback(({ snapshot }) => () => ({
    bookmarkedStops: snapshot.getLoadable(atom_bookmarkedStopsState).contents as string[],
    flyToFitStops: snapshot.getLoadable(atom_flyToFitStops).contents as
      | ((stops: Stop[], options?: { padding?: number; maxZoom?: number }) => void)
      | null,
  }), []);

  // ----- DISABLED FOR PROD — re-enable for next release: navigator (guide-route) controls -----
  // const {
  //   isNavigatorEnabled,
  //   hasActiveRoute,
  //   toggleNavigator,
  // } = useNavigatorControls();
  // ----- /DISABLED FOR PROD -----
  // Stubs so existing references in commented-out blocks still type-check if uncommented.
  const isNavigatorEnabled = false;
  const hasActiveRoute = false;
  const toggleNavigator = () => {};

  const plannedRoute = useRecoilValue(atom_plannedRoute);

  // Pill click: toggle navigation mode for routeToStop.
  // (Planned-route branch DISABLED FOR PROD — re-enable for next release.)
  // Reads isNavigationMode/isDesktop/tour config/listMode from the snapshot so MapFilters
  // doesn't subscribe to those atoms and re-render on every change.
  const handleNavigatePillClick = useRecoilCallback(({ snapshot }) => () => {
    const navMode = snapshot.getLoadable(atom_isNavigationMode).contents as boolean;
    const isDesktopNow = snapshot.getLoadable(atom_isDesktop).contents as boolean;
    const tour = snapshot.getLoadable(atom_tourState).contents as { config?: Record<string, unknown> } | null;
    // const currentListMode = snapshot.getLoadable(atom_listModeState).contents as string | null;
    const defaultListMode = isDesktopNow
      ? 'desktop' as const
      : tour?.config?.['show_list']
        ? 'mobileList' as const
        : 'mobileSwiper' as const;

    if (routeToStopState) {
      // Toggle: if route panel is open, do the same as minimize (drop to default list).
      // If minimized, reopen — route wins via ListRender's hoisted check.
      if (navMode) {
        setIsNavigationMode(false);
        setListMode(defaultListMode);
      } else {
        setIsNavigationMode(true);
        setListMode(null);
      }
    }
    // ----- DISABLED FOR PROD — re-enable for next release: planned-route branch -----
    // else if (plannedRoute) {
    //   const isPlannedPanelOpen =
    //     currentListMode === 'mobileUserPlannedTourResults' || currentListMode === 'mobileUserPlannedTour';
    //   if (isPlannedPanelOpen) {
    //     setListMode('mobileList');
    //   } else {
    //     setListMode('mobileUserPlannedTourResults');
    //   }
    // }
    // ----- /DISABLED FOR PROD -----
  }, [routeToStopState, plannedRoute, setIsNavigationMode, setListMode]);

  // Single Navigate pill, dispatching to the highest-priority active flow.
  // Priority: routeToStop > plannedRoute > guideRoute (the existing tour route).
  // ----- DISABLED FOR PROD — re-enable for next release: plannedRoute and guideRoute branches -----
  // Only routeToStop (the route-to-location popup feature) remains active for the pill.
  type NavPriority = 'routeToStop' | 'plannedRoute' | 'guideRoute' | null;
  const navPriority: NavPriority = useMemo(() => {
    if (routeToStopState) return 'routeToStop';
    // if (plannedRoute?.stopOrder?.length) return 'plannedRoute'; // DISABLED FOR PROD
    // if (hasActiveRoute) return 'guideRoute'; // DISABLED FOR PROD
    return null;
  }, [routeToStopState, plannedRoute, hasActiveRoute]);

  const firstLeg = routeToStopState?.legs?.[0];
  const steps = firstLeg?.steps;
  const currentStep = steps?.[currentNavigationStep];
  const getStepBubbleText = () => {
    if (!currentStep) return null;
    let instruction = currentStep.html_instructions || '';
    instruction = instruction.replace(/<[^>]*>/g, '');
    const distance = currentStep.distance?.text;
    if (distance && instruction) {
      return `${instruction} (${distance})`;
    }
    return instruction || null;
  };
  const stepBubbleText = getStepBubbleText();

  const setActiveStopState = useSetRecoilState(atom_activeStopState);

  // Saved mode for sidebar-disabled flows — selector_isSavedMode in filter.atom.ts
  // reads this flag (in addition to sideBarStateAtom) to drive selector_visibleStops.
  const [isSavedActive, setIsSavedActive] = useRecoilState(atom_savedFilterActive);
  const [animateSaved, setAnimateSaved] = useState(false);
  const prevSavedCountRef = useRef(totalSavedCount);


  // Detect RTL language (Hebrew)
  const isHebrew = i18n.language === 'he';

  // Get main color from tour config, fallback to default
  const mainColor = activeTour?.config?.mainColor || '#D0552E';

  // isFilterActive derived from selector_isFilterActive above

  // Check if sidebar is hidden (show saved button only when sidebar is false)
  const showSavedButton = String(activeTour?.config?.sidebar) === 'false';
  const isSavedDisabled = totalSavedCount === 0;


  // Animation effect when saved count changes (bookmarks + user-added places)
  useEffect(() => {
    if (prevSavedCountRef.current !== totalSavedCount) {
      setAnimateSaved(true);
      const timeout = setTimeout(() => {
        setAnimateSaved(false);
      }, 500);
      prevSavedCountRef.current = totalSavedCount;
      return () => clearTimeout(timeout);
    }
  }, [totalSavedCount]);

  // Auto-deactivate saved filter when total saved count becomes empty
  useEffect(() => {
    if (totalSavedCount === 0 && isSavedActive) {
      setIsSavedActive(false);
    }
  }, [totalSavedCount, isSavedActive, setIsSavedActive]);

  // Handle filter bubble button click - toggles filter mode
  const handleFilterBubbleClick = () => {
    // If saved is active, deactivate it first
    if (isSavedActive) {
      setIsSavedActive(false);
    }

    if (isFilterActive) {
      // If already in filter mode, go back to default
      setListMode('desktop');
    } else {
      setListMode('filter');
    }
  };

  // Delegate saved-bounds fly to useMapNavigation's flyToFitStops (via atom).
  // That path handles single vs multi, supercluster-aware zoom, topBar + CTA/list
  // padding in one place — so behavior matches the rest of the app.
  const flyToSavedBounds = () => {
    const { bookmarkedStops, flyToFitStops } = getHandlerAtoms();
    if (!flyToFitStops) return;
    const bookmarkedStopObjects = (activeTour?.stops || []).filter((stop: Stop) =>
      bookmarkedStops.includes(stop.id)
    );
    if (bookmarkedStopObjects.length === 0) return;
    flyToFitStops(bookmarkedStopObjects, { padding: 50, maxZoom: 15 });
  };


  // Handle saved bubble click - toggles atom_savedFilterActive so selector_visibleStops
  // filters the tour to bookmarked stops. Independent of sideBarStateAtom because this
  // bubble only shows when the sidebar is disabled.
  const handleSavedClick = () => {
    if (isSavedDisabled) return;

    if (isSavedActive) {
      setIsSavedActive(false);
    } else {
      setIsSavedActive(true);

      // If filter mode was active, exit it
      if (isFilterActive) {
        setListMode('desktop');
        // Defer the fly so the panel that mounts after listMode change has time
        // to measure itself before flyToFitStops reads the bottom-obstruction.
        flyAfterLayout(flyToSavedBounds);
      } else {
        // No listMode change → atom is already accurate, fly directly.
        flyToSavedBounds();
      }
    }
  };

  return (
    <Box
      component="nav"
      sx={{
        // Hebrew: position on right with basic padding, non-Hebrew: position on left
        // Note: Parent container already accounts for sidebarWidth, so we only add 84px offset here
        ...(isHebrew
          ? { marginRight: { xs: '16px', md: '16px' }, marginLeft: 'auto' }
          : { marginLeft: { xs: '16px', md: '100px' } }
        ),
        background: 'transparent',
        py: 0.5,
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        width: 'fit-content',
      }}
    >
      {/* Filter button with icon and text */}
      <Box
        onClick={handleFilterBubbleClick}
        role="button"
        tabIndex={0}
        aria-label={t('filter') || 'Filter'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          borderRadius: '20px',
          backgroundColor: isFilterActive ? mainColor : 'white',
          color: isFilterActive ? 'white' : '#666',
          border: '1px solid',
          borderColor: isFilterActive ? mainColor : 'rgba(230,204,241,1)',
          cursor: 'pointer',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            backgroundColor: isFilterActive ? mainColor : '#f5f5f5',
            opacity: isFilterActive ? 0.85 : 1,
          },
        }}
      >
        <i
          className={isFilterActive ? 'fa-solid fa-filter' : 'fa-regular fa-filter'}
          style={{ fontSize: '14px' }}
        />
        <span style={{ fontSize: '14px', fontWeight: 500 }}>
          {t('filter')}
        </span>
      </Box>

      {/* Search - isolated component, keystrokes don't re-render MapFilters */}
      <MapStopSearch stops={activeTour?.stops || []} mainColor={mainColor} />

      {/* Saved button bubble - only shown when sidebar is hidden */}
      {showSavedButton && (
        <Box
          onClick={handleSavedClick}
          role="button"
          tabIndex={isSavedDisabled ? -1 : 0}
          aria-label={t('saved') || 'Saved'}
          aria-disabled={isSavedDisabled}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            borderRadius: '20px',
            backgroundColor: isSavedActive || animateSaved ? mainColor : 'white',
            color: isSavedActive || animateSaved ? 'white' : isSavedDisabled ? '#ccc' : '#666',
            border: '1px solid',
            borderColor: isSavedActive || animateSaved ? mainColor : 'rgba(230,204,241,1)',
            cursor: isSavedDisabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease-in-out',
            transform: animateSaved ? 'scale(1.1)' : 'scale(1)',
            opacity: isSavedDisabled ? 0.6 : 1,
            '&:hover': {
              backgroundColor: isSavedDisabled
                ? 'white'
                : (isSavedActive || animateSaved)
                  ? mainColor
                  : '#f5f5f5',
              opacity: (isSavedActive || animateSaved) && !isSavedDisabled ? 0.85 : undefined,
            },
          }}
        >
          <Badge
            badgeContent={totalSavedCount > 0 ? totalSavedCount : null}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '9px',
                minWidth: '14px',
                height: '14px',
                top: -4,
                right: -4,
              },
            }}
          >
            <i
              className={(isSavedActive || animateSaved) ? "fa-solid fa-heart" : "fa-regular fa-heart"}
              style={{ fontSize: '14px', transition: 'all 0.3s ease' }}
            />
          </Badge>
          <span style={{ fontSize: '14px', fontWeight: 500 }}>
            {t('saved') || 'Saved'}
          </span>
        </Box>
      )}

      {/* ----- DISABLED FOR PROD — re-enable for next release: guide-route Navigate button ----- */}
      {/* Guide-route only (no specific target): original Navigator button — toggles location tracking. */}
      {/*
      {navPriority === 'guideRoute' && (
        <Box
          onClick={toggleNavigator}
          role="button"
          tabIndex={0}
          aria-label={isNavigatorEnabled ? t('navigator.tracking') : t('navigator.navigate')}
          aria-pressed={isNavigatorEnabled}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            borderRadius: '20px',
            backgroundColor: isNavigatorEnabled ? mainColor : 'white',
            color: isNavigatorEnabled ? 'white' : '#666',
            border: '1px solid',
            borderColor: isNavigatorEnabled ? mainColor : 'rgba(230,204,241,1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease-in-out',
            animation: isNavigatorEnabled ? 'navigatorPulse 2s infinite' : 'none',
            '@keyframes navigatorPulse': {
              '0%': { boxShadow: `0 0 0 0 ${mainColor}40` },
              '70%': { boxShadow: `0 0 0 8px ${mainColor}00` },
              '100%': { boxShadow: `0 0 0 0 ${mainColor}00` },
            },
            '&:hover': {
              backgroundColor: isNavigatorEnabled ? mainColor : '#f5f5f5',
              opacity: isNavigatorEnabled ? 0.85 : 1,
            },
          }}
        >
          <i
            className={isNavigatorEnabled ? 'fa-solid fa-location-arrow' : 'fa-regular fa-location-arrow'}
            style={{ fontSize: '14px' }}
          />
          <span style={{ fontSize: '14px', fontWeight: 500 }}>
            {isNavigatorEnabled ? t('navigator.tracking') : t('navigator.navigate')}
          </span>
        </Box>
      )}
      */}
      {/* ----- /DISABLED FOR PROD ----- */}

      {/* Active target: green pill with the target.
          - routeToStop: gated by showNavigationBubble (set when fetch succeeds)
          - plannedRoute: DISABLED FOR PROD — re-enable for next release
          Styling toggles between active (green bg / white text+dot) and inactive (white bg /
          green border+dot+dark text) based on atom_isNavigationMode. NavigatePill subscribes
          to that atom in isolation so MapFilters doesn't re-render on toggle. */}
      {(navPriority === 'routeToStop' && showNavigationBubble) /* || navPriority === 'plannedRoute' */ && (
        <NavigatePill
          label={
            navPriority === 'routeToStop'
              ? (routeToStopState?.stopTitle || stepBubbleText || t('navigate_mode') || 'Navigate mode')
              : (t('planned_route') || 'Planned route')
          }
          ariaLabel={t('navigate_mode') || 'Navigate mode'}
          onClick={handleNavigatePillClick}
        />
      )}

      {/* Navigation mode bubble - shown after first minimize, stays until End Ride */}
      {/* Debug bubble - commented out
      {(
        <Box
          onClick={() => {
            const panel = document.getElementById('passive-debug-panel');
            if (panel) panel.style.display = panel.style.display === 'none' ? 'flex' : 'none';
            else {
              const event = new CustomEvent('toggle-debug-panel');
              window.dispatchEvent(event);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Debug"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            borderRadius: '20px',
            backgroundColor: '#1a1a2e',
            color: '#0f0',
            border: '1px solid #0f0',
            cursor: 'pointer',
            fontFamily: 'monospace',
            transition: 'all 0.3s ease-in-out',
            '&:hover': { opacity: 0.85 },
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 700 }}>DBG</span>
        </Box>
      )}
      */}

      {/* Debug panel — commented out
      <PassiveDataDebugPanel />
      */}
    </Box>
  );
});

// Isolated pill component: subscribes to atom_isNavigationMode alone so toggling
// navigation mode only re-renders this 30-line component instead of all of MapFilters.
type NavigatePillProps = {
  label: string;
  ariaLabel: string;
  onClick: () => void;
};
const NavigatePill = React.memo(function NavigatePill({ label, ariaLabel, onClick }: NavigatePillProps) {
  const isNavigationMode = useRecoilValue(atom_isNavigationMode);
  const dotColor = isNavigationMode ? 'white' : '#4CAF50';
  const dotGlow = isNavigationMode ? '255, 255, 255' : '76, 175, 80';
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={isNavigationMode}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: '20px',
        backgroundColor: isNavigationMode ? '#4CAF50' : 'white',
        color: isNavigationMode ? 'white' : '#333',
        border: '1px solid',
        borderColor: '#4CAF50',
        cursor: 'pointer',
        transition: 'all 0.3s ease-in-out',
        maxWidth: '250px',
        '&:hover': { backgroundColor: isNavigationMode ? '#43A047' : '#f5f5f5' },
      }}
    >
      <Box
        sx={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
          animation: 'flicker 1.5s ease-in-out infinite',
          '@keyframes flicker': {
            '0%, 100%': {
              opacity: 1,
              boxShadow: `0 0 8px 2px rgba(${dotGlow}, 0.6)`,
            },
            '50%': {
              opacity: 0.5,
              boxShadow: `0 0 4px 1px rgba(${dotGlow}, 0.3)`,
            },
          },
        }}
      />
      <span style={{
        fontSize: '14px',
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </span>
    </Box>
  );
});
